/* eslint-disable @typescript-eslint/indent */
import React from 'react';
import { useGetPaginationByType } from 'entity/comment/commentHooks';
import Link from 'components/misc/Link';
import NextImage from 'next/image';
import he from 'he';
import { useAmp } from 'next/amp';
import { generateArticleLink } from 'utils/generateShareLink';
import PostCommentForm from 'components/comment/PostCommentForm';
import ArticleEngagement from 'components/blog/ArticleItemNew/ArticleEngagement';
import CommentList from 'components/comment/CommentList';
import HTML from 'html-parse-stringify2';
import get from 'lodash/get';
import AdSlot from 'app/components/misc/AdSlot';
import { ArticleWithLink } from 'app/entity/blog/blogTypes';

import { Article, MainContent, CategoriesWrapper, Title, Body } from './styles';
import Byline from '../../Byline';
import AuthorProfile from '../../AuthorProfile';

const whitelistedAttrs = [
  'src',
  'sizes',
  'srcset',
  'src',
  'width',
  'height',
  'style',
];

const AmpImage = ({ src, alt }) => {
  return (
    // @ts-ignore: amp-img exist for amp this can be ignore
    <amp-img
      src={src}
      alt={alt}
      width="1200px"
      height="628px"
      layout="responsive"
    />
  );
};

interface OwnProps {
  article: ArticleWithLink;
  commentSectionRef?: React.ReactNode;
  // this is article ID from db not wordpress
  articleOwnId?: number;
}

const ArticleDetail = (props: OwnProps) => {
  const { article, commentSectionRef, articleOwnId } = props;
  const {
    categories,
    id,
    title,
    imageUrl,
    imageAlt,
    author,
    publishDate,
    content,
    slug,
  } = article;

  const isAmp = useAmp();

  const articlePagination = useGetPaginationByType('Article', articleOwnId);
  const { pagination } = articlePagination;

  const parsedContent = content
    ? content
        .replace(/<table/g, '<div class="table-wrapper"><table')
        .replace(/<\/table>/g, '</table></div>')
        .replace(/style="[^"]*"/g, '')
        .replace(/<figure/g, '<figure style="min-width:auto"')
        .replace(/(<img[^>]+>.*?<\/img>|<img[^>]+>)/g, html => {
          const ast = HTML.parse(html);
          const attrHash = ast[0].attrs;
          const attrList = Object.keys(attrHash).map(key => ({
            key,
            value: attrHash[key],
          }));
          const attrStr = attrList.reduce((acc, item) => {
            if (whitelistedAttrs.indexOf(item.key) === -1) return acc;
            return `${acc} ${item.key}="${item.value}"`;
          }, '');
          return `<img loading="lazy" ${attrStr}/>`;
        })
    : '';

  const ampContent = parsedContent
    .replace(/<figure/g, '<figure style="min-width:auto;display:flex;"')
    .replace(/<ul[^>]+>/g, '<ul>')
    // only allow valid amp-img attributes
    .replace(/(<img[^>]+>.*?<\/img>|<img[^>]+>)/g, html => {
      const ast = HTML.parse(html);
      const attrHash = ast[0].attrs;
      const attrList = Object.keys(attrHash).map(key => ({
        key,
        value: attrHash[key],
      }));
      const attrStr = attrList.reduce((acc, item) => {
        if (whitelistedAttrs.indexOf(item.key) === -1) return acc;
        return `${acc} ${item.key}="${item.value}"`;
      }, '');
      const hasWidthAndHeightAttrs =
        attrList.find(item => item.key === 'width') &&
        attrList.find(item => item.key === 'height');
      if (!hasWidthAndHeightAttrs) {
        return `
          <div class="fixed-height-container">
            <amp-img class="contain" layout="fill" style="margin:auto;max-width:100%;" ${attrStr}></amp-img>
          </div>
        `;
      }
      return `<amp-img layout="intrinsic" style="margin:auto;max-width:100%;" ${attrStr}></amp-img>`;
    })
    .replace(/<iframe[^>]+>.*?<\/iframe>/g, html => {
      const ast = HTML.parse(html);
      const src = get(ast, '[0].attrs.src');
      if (!src) return '';

      let height = parseInt(get(ast, '[0].attrs.height', 325), 10);
      if (isNaN(height)) height = 325;

      return `<amp-iframe src="${src}" sandbox="allow-scripts allow-popups allow-forms allow-top-navigation" height="${height}" frameborder="0"></amp-iframe>`;
    });

  return (
    <Article>
      {isAmp ? (
        <AmpImage alt={imageAlt} src={imageUrl} />
      ) : (
        <NextImage
          src={imageUrl}
          alt={imageAlt}
          layout="responsive"
          width={660}
          height={346.5}
          priority
        />
      )}

      <MainContent>
        <div>
          <CategoriesWrapper>
            {categories.map((c, i) => (
              <Link as={c.link.as} href={c.link.href} key={i}>
                <a>{he.decode(c.name)}</a>
              </Link>
            ))}
          </CategoriesWrapper>
          <Title dangerouslySetInnerHTML={{ __html: title }} />
          <Byline timeagoDisabled author={author} publishDate={publishDate} />
        </div>
        <Body
          dangerouslySetInnerHTML={{
            __html: isAmp ? ampContent : parsedContent,
          }}
        />
        <AdSlot type="blog" slotKey="bottom-rectangle" />
        <AuthorProfile {...author} />
        {commentSectionRef}
        {articleOwnId && (
          <div className="flex flex-col w-full items-center gap-4">
            <div className="horizontal-divider" />
            <ArticleEngagement
              articleSlug={slug}
              articleId={id}
              commentsCount={pagination.totalCount}
            />
            <div className="horizontal-divider" />
            <PostCommentForm
              commentableType="Article"
              commentableId={articleOwnId}
            />
            <CommentList
              totalCommentCount={pagination.totalCount}
              commentableId={articleOwnId}
              commentableType="Article"
              childCommentableType="ArticleComment"
              commentShareLink={generateArticleLink(slug)}
            />
          </div>
        )}
      </MainContent>
    </Article>
  );
};

export default ArticleDetail;
